"use client";

import React, { useEffect, useState, useMemo } from "react";
import Link from "next/link";
import Image from "next/image";

interface Blog {
  ID: number;
  Title: string;
  Excerpt: string;
  Slug: string;
  Attachment: string;
  UpdateDate: string;
  UpdatedBy?: string;
}

interface BlogResponse {
  blogs: Blog[];
  total: number;
}

interface ClientComponentProps {
  currentPage: number;
  /** Posts already fetched on the server for this page; when present the client does not refetch. */
  initialBlogs?: Blog[];
  initialTotal?: number;
}

export default function ClientComponent({ currentPage, initialBlogs, initialTotal }: ClientComponentProps) {
  const [blogs, setBlogs] = useState<Blog[]>(initialBlogs ?? []);
  const [isLoading, setIsLoading] = useState(!initialBlogs);
  const [error, setError] = useState<string | null>(null);
  const [totalBlogs, setTotalBlogs] = useState(initialTotal ?? 0);
  const blogsPerPage = 9; // 3-column grid
  const imageBaseUrl = process.env.NEXT_PUBLIC_IMAGE_URL || "https://lit-000.litfusion.litpl.com";
  const [searchTerm, setSearchTerm] = useState("");

  // Debug logging for production issues
  if (process.env.NODE_ENV === "development") {
    console.log("ClientComponent received currentPage:", currentPage);
  }

  useEffect(() => {
    if (initialBlogs) return; // server already provided this page
    const fetchBlogs = async () => {
      setIsLoading(true);
      try {
        const apiUrl = `/api/blog?page=${currentPage}&count=${blogsPerPage}&_t=${Date.now()}`;

        // Debug logging for production issues
        if (process.env.NODE_ENV === "development") {
          console.log("Fetching blogs from:", apiUrl);
        }

        const res = await fetch(apiUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          cache: "no-store",
        });

        if (!res.ok) {
          const errorText = await res.text();
          console.error(`Failed to fetch blogs: ${res.status} ${res.statusText}`, errorText);
          throw new Error(`Failed to fetch blogs: ${res.status} ${res.statusText}`);
        }

        const data: BlogResponse | Blog[] = await res.json();
        
        if (process.env.NODE_ENV === "development") {
          console.log("API Response received:", JSON.stringify(data, null, 2));
        }
        
        // Handle different response formats
        let blogsArray: Blog[] = [];
        let total = 0;
        
        if (Array.isArray(data)) {
          // If response is directly an array
          blogsArray = data;
          total = data.length;
        } else if (data.blogs && Array.isArray(data.blogs)) {
          // If response has blogs property
          blogsArray = data.blogs;
          total = data.total || data.blogs.length;
        } else if ((data as any).data && Array.isArray((data as any).data)) {
          // If response has data property
          blogsArray = (data as any).data;
          total = (data as any).total || (data as any).data.length;
        } else if (process.env.NODE_ENV === "development") {
          console.warn("Unexpected response format:", data);
        }
        setBlogs(blogsArray);
        setTotalBlogs(total);
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : "Unknown error occurred";
        setError(`Failed to load blogs: ${errorMessage}`);
        console.error(`Failed to fetch blogs for page ${currentPage}:`, errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBlogs();
  }, [currentPage, initialBlogs]);

  const totalPages = Math.ceil(totalBlogs / blogsPerPage);

  // Memoize filtered blogs for performance
  const filteredBlogs = useMemo(() => {
    if (!searchTerm.trim()) return blogs;
    const lowerSearch = searchTerm.toLowerCase();
    return blogs.filter(
      (blog) =>
        blog.Title.toLowerCase().includes(lowerSearch) ||
        (blog.Excerpt && blog.Excerpt.toLowerCase().includes(lowerSearch))
    );
  }, [blogs, searchTerm]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="container mx-auto px-5 py-10 sm:px-8">
      <h2 className="text-3xl font-bold mb-8">Latest from the LITPL blog</h2>

      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <label htmlFor="blog-search" className="sr-only">
          Search blogs
        </label>
        <input
          id="blog-search"
          type="search"
          placeholder="Search blogs..."
          className="w-full md:w-1/3 p-2 border border-gray-300 rounded shadow-sm focus:ring-2 focus:ring-blue-200 focus:outline-none"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          aria-label="Search blog posts"
        />
      </div>

      {isLoading ? (
        <div className="text-center text-gray-600">
          <div className="animate-pulse space-y-4">
            {[...Array(blogsPerPage)].map((_, i) => (
              <div key={i} className="h-64 bg-gray-200 rounded-lg" />
            ))}
          </div>
        </div>
      ) : error ? (
        <p className="text-center text-red-600">Error: {error}</p>
      ) : filteredBlogs.length === 0 ? (
        <div className="text-center py-10">
          <p className="text-xl text-gray-600">
            {blogs.length === 0 
              ? "No blog posts found." 
              : `No blog posts match "${searchTerm}". Showing ${blogs.length} total blogs.`}
          </p>
          {process.env.NODE_ENV === "development" && (
            <div className="mt-4 text-sm text-gray-500">
              <p>Debug Info:</p>
              <p>Total blogs from API: {totalBlogs}</p>
              <p>Blogs in state: {blogs.length}</p>
              <p>Filtered blogs: {filteredBlogs.length}</p>
              <p>Search term: "{searchTerm}"</p>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredBlogs.map((blog) => (
            <div key={blog.ID} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300 flex flex-col">
              {blog.Attachment && (
                <div className="relative h-48 w-full bg-gray-100 overflow-hidden">
                  <Image
                    src={`${imageBaseUrl}${blog.Attachment.startsWith("/") ? blog.Attachment : `/${blog.Attachment}`}`}
                    alt={blog.Title}
                    fill
                    className="object-cover"
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    quality={75}
                    loading="lazy"
                    placeholder="blur"
                    blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q=="
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = "none";
                    }}
                  />
                </div>
              )}
              <div className="p-6 flex flex-col flex-1">
                <p className="text-xs text-gray-500 mb-2">{formatDate(blog.UpdateDate || "")}</p>
                <Link href={`/blog/${blog.Slug}`}>
                  <h2 className="text-lg font-bold mb-2 hover:text-blue-700 transition-colors line-clamp-2">{blog.Title}</h2>
                </Link>
                <p className="text-gray-700 mb-4 line-clamp-3 flex-1">{blog.Excerpt || ""}</p>
                <Link href={`/blog/${blog.Slug}`} className="mt-auto inline-block text-blue-600 hover:underline font-medium">
                  Read More →
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
      <div className="flex justify-center items-center gap-2 mt-10">
          {currentPage > 1 ? (
            <Link 
              href={currentPage === 2 ? "/blog" : `/blog?page=${currentPage - 1}`} 
              className="px-4 py-2 rounded border font-semibold transition-colors bg-white text-blue-600 border-blue-200 hover:bg-blue-50"
            >
          Previous
        </Link>
          ) : (
            <span className="px-4 py-2 rounded border font-semibold bg-gray-200 text-gray-400 cursor-not-allowed">
              Previous
            </span>
          )}
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
            <Link
              key={pageNum}
              href={pageNum === 1 ? "/blog" : `/blog?page=${pageNum}`}
              className={`px-4 py-2 rounded border font-semibold transition-colors ${
                pageNum === currentPage
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-blue-600 border-blue-200 hover:bg-blue-50"
              }`}
            >
              {pageNum}
            </Link>
          ))}
          {currentPage < totalPages ? (
            <Link 
              href={`/blog?page=${currentPage + 1}`} 
              className="px-4 py-2 rounded border font-semibold transition-colors bg-white text-blue-600 border-blue-200 hover:bg-blue-50"
            >
          Next
        </Link>
          ) : (
            <span className="px-4 py-2 rounded border font-semibold bg-gray-200 text-gray-400 cursor-not-allowed">
              Next
            </span>
          )}
      </div>
      )}
    </div>
  );
}
